import React, { useState, useEffect, useRef } from 'react';
import {
  createBox,
  updateBox,
  addFotos,
  deleteFoto,
  analyzeFoto,
  fetchBox,
  fetchNextBoxNumber,
} from '../api';

const DACHBODEN_BEREICHE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function parseStandort(value) {
  const match = String(value || '').toUpperCase().match(/^DB-([A-Z])-(\d{2})-E(\d{1,2})$/);
  if (!match) return null;
  return { bereich: match[1], platz: String(Number.parseInt(match[2], 10)), ebene: match[3] };
}

function buildStandort(bereich, platz, ebene) {
  const parsedPlatz = Number.parseInt(platz, 10);
  const parsedEbene = Number.parseInt(ebene, 10);
  if (!bereich || !Number.isInteger(parsedPlatz) || parsedPlatz < 1 || parsedPlatz > 99) return '';
  if (!Number.isInteger(parsedEbene) || parsedEbene < 1 || parsedEbene > 9) return '';
  return `DB-${bereich}-${String(parsedPlatz).padStart(2, '0')}-E${parsedEbene}`;
}

export default function BoxForm({ box, initialBoxNumber = '', kategorien, onSaved, onCancel }) {
  const [currentBox, setCurrentBox] = useState(box);
  const isEdit = !!currentBox;

  const [boxNummer, setBoxNummer] = useState(box?.box_nummer || initialBoxNumber || '');
  const [parentBox, setParentBox] = useState(box?.parent_box || '');
  const [kategorie, setKategorie] = useState(box?.kategorie || '');
  const [inhalt, setInhalt] = useState(box?.inhalt_stichworte || '');
  const [erinnerungAm, setErinnerungAm] = useState(
    box?.erinnerung_am ? String(box.erinnerung_am).slice(0, 10) : ''
  );
  const [erinnerungText, setErinnerungText] = useState(box?.erinnerung_text || '');
  const parsedStandort = parseStandort(box?.standort);
  const [standortBereich, setStandortBereich] = useState(parsedStandort?.bereich || '');
  const [standortPlatz, setStandortPlatz] = useState(parsedStandort?.platz || '');
  const [standortEbene, setStandortEbene] = useState(parsedStandort?.ebene || '1');
  const [standortLegacy] = useState(box?.standort && !parsedStandort ? box.standort : '');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showKatList, setShowKatList] = useState(false);
  const katRef = useRef();

  const [fotos, setFotos] = useState(box?.fotos || []);
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [kiMap, setKiMap] = useState({});
  const [kiLoading, setKiLoading] = useState(null);
  const [fotoInhaltMap, setFotoInhaltMap] = useState({}); // fotoId → übernommener Text
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const fileInputRef = useRef();
  const cameraInputRef = useRef();

  const filteredKat = kategorien.filter(k =>
    k.toLowerCase().includes(kategorie.toLowerCase()) && k !== kategorie
  );
  const standortCode = buildStandort(standortBereich, standortPlatz, standortEbene);

  useEffect(() => {
    if (box || initialBoxNumber) return undefined;
    let active = true;
    fetchNextBoxNumber()
      .then(data => {
        if (active) setBoxNummer(current => current || data.box_nummer || '');
      })
      .catch(err => {
        if (active) setError('Nächste Boxnummer konnte nicht geladen werden: ' + err.message);
      });
    return () => { active = false; };
  }, [box, initialBoxNumber]);

  useEffect(() => {
    const handler = (e) => {
      if (katRef.current && !katRef.current.contains(e.target)) setShowKatList(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function handleNewFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const previews = files.map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setNewFiles(prev => [...prev, ...files]);
    setNewPreviews(prev => [...prev, ...previews]);
    e.target.value = '';
  }

  function removeNewFile(index) {
    URL.revokeObjectURL(newPreviews[index].preview);
    setNewFiles(prev => prev.filter((_, i) => i !== index));
    setNewPreviews(prev => prev.filter((_, i) => i !== index));
  }

  async function handleDeleteFoto(fotoId) {
    if (!window.confirm('Dieses Foto wirklich löschen?')) return;
    try {
      await deleteFoto(fotoId);
      const updatedFotos = fotos.filter(f => f.id !== fotoId);
      setFotos(updatedFotos);
      setKiMap(prev => { const next = { ...prev }; delete next[fotoId]; return next; });
      // Zugehörigen KI-Text aus Inhalt entfernen
      if (fotoInhaltMap[fotoId]) {
        const textToRemove = fotoInhaltMap[fotoId];
        setInhalt(prev => {
          let updated = prev.replace(', ' + textToRemove, '');
          updated = updated.replace(textToRemove + ', ', '');
          updated = updated.replace(textToRemove, '');
          return updated.trim().replace(/,\s*$/, '').replace(/^,\s*/, '');
        });
        setFotoInhaltMap(prev => { const next = { ...prev }; delete next[fotoId]; return next; });
      }
      if (currentBox) onSaved({ ...currentBox, fotos: updatedFotos });
    } catch (err) {
      setError('Foto löschen fehlgeschlagen: ' + err.message);
    }
  }

  async function handleKiAnalyse(fotoId) {
    setKiLoading(fotoId);
    setError('');
    setSaveSuccess(false);
    try {
      const v = await analyzeFoto(fotoId);
      setKiMap(prev => ({ ...prev, [fotoId]: v }));
    } catch (err) {
      setError('KI-Analyse fehlgeschlagen: ' + err.message);
    } finally {
      setKiLoading(null);
    }
  }

  function applyKi(fotoId) {
    const v = kiMap[fotoId];
    if (!v) return;
    // Kategorie nur setzen wenn noch leer
    if (v.kategorie && !kategorie.trim()) setKategorie(v.kategorie);
    // Inhalt anhängen statt überschreiben
    if (v.stichworte) {
      setInhalt(prev => {
        const existing = prev.trim();
        return existing ? existing + ', ' + v.stichworte : v.stichworte;
      });
      setFotoInhaltMap(prev => ({ ...prev, [fotoId]: v.stichworte }));
    }
    setKiMap(prev => { const next = { ...prev }; delete next[fotoId]; return next; });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!boxNummer.trim()) { setError('Box-Nummer ist Pflichtfeld'); return; }
    setSaving(true);
    setError('');
    setSaveSuccess(false);

    let saved;
    try {
      const fd = new FormData();
      fd.append('box_nummer', boxNummer.trim().toUpperCase());
      fd.append('parent_box', parentBox.trim());
      fd.append('kategorie', kategorie.trim());
      fd.append('inhalt_stichworte', inhalt.trim());
      fd.append('standort', standortCode || standortLegacy.trim());
      fd.append('erinnerung_am', erinnerungAm);
      fd.append('erinnerung_text', erinnerungText.trim());
      saved = currentBox ? await updateBox(currentBox.id, fd) : await createBox(fd);
    } catch (err) {
      setError(err.message);
      setSaving(false);
      return;
    }

    if (newFiles.length > 0) {
      try {
        const fotoFd = new FormData();
        newFiles.forEach(f => fotoFd.append('fotos', f));
        await addFotos(saved.id, fotoFd);
      } catch (err) {
        setError('Foto-Upload fehlgeschlagen: ' + err.message);
      }
    }

    try {
      const fullBox = await fetchBox(saved.id);
      setCurrentBox(fullBox);
      setFotos(fullBox.fotos || []);
      setNewFiles([]);
      setNewPreviews(prev => { prev.forEach(p => URL.revokeObjectURL(p.preview)); return []; });
      setSaveSuccess(true);
      onSaved(fullBox);
    } catch {
      setCurrentBox(saved);
      setNewFiles([]);
      setNewPreviews([]);
      setSaveSuccess(true);
      onSaved(saved);
    } finally {
      setSaving(false);
    }
  }

  const s = styles;
  const hasKiSuggestion = Object.keys(kiMap).length > 0;

  return (
    <div className="box-form-overlay" style={s.overlay}>
      {lightboxUrl && (
        <div style={s.lightbox} onClick={() => setLightboxUrl(null)}>
          <button style={s.lightboxClose} onClick={() => setLightboxUrl(null)}>✕</button>
          <img
            src={lightboxUrl}
            alt="Foto"
            style={s.lightboxImg}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      <div className="box-form-modal" style={s.modal}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.kicker}>{currentBox ? 'Box bearbeiten' : 'Neue Box'}</div>
            <h2 style={s.title}>
              {currentBox ? currentBox.box_nummer : 'Inhalt dokumentieren'}
            </h2>
          </div>
          <button type="button" onClick={onCancel} style={s.closeButton} aria-label="Schließen">×</button>
        </div>

        {error && <div style={s.error}>{error}</div>}
        {saveSuccess && !error && <div style={s.successMsg}>✓ Gespeichert</div>}
        {kiLoading !== null && (
          <div style={s.kiLoadingBar}>
            <span style={s.kiSpinner}>⏳</span> KI analysiert Foto… bitte warten (ca. 15–30 Sek.)
          </div>
        )}

        <form onSubmit={handleSubmit} style={s.form} noValidate>
          <label style={s.label}>
            Box-Nummer *
            <input
              style={s.input}
              value={boxNummer}
              placeholder="wird automatisch vergeben"
              disabled
            />
            <small style={s.hint}>Die dauerhafte Boxnummer wird automatisch fortlaufend vergeben.</small>
          </label>

          <label style={s.label}>
            Übergeordnete Box (optional)
            <input
              style={s.input}
              value={parentBox}
              onChange={e => setParentBox(e.target.value)}
              placeholder="z.B. B05"
            />
          </label>

          <label style={s.label}>
            Kategorie
            <div style={{ position: 'relative' }} ref={katRef}>
              <input
                style={s.input}
                value={kategorie}
                onChange={e => { setKategorie(e.target.value); setShowKatList(true); }}
                onFocus={() => setShowKatList(true)}
                placeholder="z.B. Werkzeug, Weihnachten, Dokumente"
              />
              {showKatList && filteredKat.length > 0 && (
                <div style={s.dropdown}>
                  {filteredKat.map(k => (
                    <div key={k} style={s.dropdownItem}
                      onMouseDown={() => { setKategorie(k); setShowKatList(false); }}>
                      {k}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </label>

          <label style={s.label}>
            Inhalt / Stichworte
            <textarea
              style={{ ...s.input, minHeight: 80, resize: 'vertical' }}
              value={inhalt}
              onChange={e => setInhalt(e.target.value)}
              placeholder="z.B. Schrauben, Dübel, Kleinwerkzeug"
            />
          </label>

          <div style={s.reminderBox}>
            <div style={s.reminderHeader}>
              <div>
                <div style={s.reminderTitle}>Erinnerung (optional)</div>
                <div style={s.reminderIntro}>Zum Beispiel, wenn eine Verpackung nach der Gewährleistung weg kann.</div>
              </div>
              {(erinnerungAm || erinnerungText) && (
                <button
                  type="button"
                  style={s.reminderClear}
                  onClick={() => { setErinnerungAm(''); setErinnerungText(''); }}
                >
                  Entfernen
                </button>
              )}
            </div>
            <div className="box-reminder-grid" style={s.reminderGrid}>
              <label style={s.label}>
                Erinnern am
                <input
                  style={s.input}
                  type="date"
                  value={erinnerungAm}
                  onChange={e => setErinnerungAm(e.target.value)}
                />
              </label>
              <label style={s.label}>
                Hinweis
                <input
                  style={s.input}
                  value={erinnerungText}
                  onChange={e => setErinnerungText(e.target.value)}
                  maxLength="255"
                  placeholder="z.B. Verpackung kann entsorgt werden"
                />
              </label>
            </div>
            <small style={s.hint}>Fällige Erinnerungen erscheinen deutlich sichtbar in der Übersicht.</small>
          </div>

          <div style={s.locationBox}>
            <div style={s.locationTitle}>Dachbodenplatz (optional)</div>
            {standortLegacy && !standortCode && (
              <div style={s.legacyLocation}>
                Bisheriger Standort: <strong>{standortLegacy}</strong>. Für einen genauen Platz bitte neu auswählen.
              </div>
            )}
            <div className="box-location-grid" style={s.locationGrid}>
              <label style={s.label}>
                Bereich
                <select
                  style={s.input}
                  value={standortBereich}
                  onChange={e => setStandortBereich(e.target.value)}
                >
                  <option value="">–</option>
                  {DACHBODEN_BEREICHE.map(value => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label style={s.label}>
                Stellfeld
                <input
                  style={s.input}
                  type="number"
                  min="1"
                  max="99"
                  inputMode="numeric"
                  value={standortPlatz}
                  onChange={e => setStandortPlatz(e.target.value)}
                  placeholder="01"
                />
              </label>
              <label style={s.label}>
                Ebene im Stapel
                <select
                  style={s.input}
                  value={standortEbene}
                  onChange={e => setStandortEbene(e.target.value)}
                >
                  {[1, 2, 3, 4, 5].map(value => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
            </div>
            {standortCode && <div style={s.locationPreview}>Standort: <strong>{standortCode}</strong></div>}
            <small style={s.hint}>Ebene 1 ist die unterste Box im Stapel.</small>
          </div>

          <div style={s.label}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
              Fotos {fotos.length + newFiles.length > 0 ? `(${fotos.length + newFiles.length})` : ''}
            </span>
            <div style={s.fotoGrid}>
              {fotos.map(foto => (
                <div key={foto.id} style={s.fotoThumbWrap}>
                  <img
                    src={foto.url}
                    alt="Foto"
                    style={s.fotoThumb}
                    onClick={() => setLightboxUrl(foto.url)}
                  />
                  <button type="button" style={s.btnDeleteFoto}
                    onClick={() => handleDeleteFoto(foto.id)} title="Foto löschen">×</button>
                  <button
                    type="button"
                    style={s.btnKiThumb}
                    onClick={() => handleKiAnalyse(foto.id)}
                    disabled={kiLoading === foto.id}
                    title="KI-Analyse"
                  >
                    {kiLoading === foto.id ? '…' : '🤖'}
                  </button>
                  {kiMap[foto.id] && (
                    <div style={s.kiPopup}>
                      <div style={s.kiPopupText}>
                        <strong>{kiMap[foto.id].kategorie}</strong>
                        <span style={{ color: '#374151' }}> · {kiMap[foto.id].stichworte}</span>
                      </div>
                      <div style={s.kiPopupBtns}>
                        <button type="button" style={s.btnApply} onClick={() => applyKi(foto.id)}>
                          Übernehmen
                        </button>
                        <button type="button" style={s.btnIgnore}
                          onClick={() => setKiMap(prev => { const n = { ...prev }; delete n[foto.id]; return n; })}>
                          ✕
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {newPreviews.map((p, i) => (
                <div key={`new-${i}`} style={{ ...s.fotoThumbWrap, opacity: 0.7 }}>
                  <img src={p.preview} alt="Neu" style={s.fotoThumb}
                    onClick={() => setLightboxUrl(p.preview)} />
                  {/\.(heic|heif)$/i.test(p.file.name) && (
                    <div style={s.heicBadge}>iPhone-Foto<br />wird umgewandelt</div>
                  )}
                  <button type="button" style={s.btnDeleteFoto}
                    onClick={() => removeNewFile(i)} title="Entfernen">×</button>
                  <div style={s.newBadge}>neu</div>
                </div>
              ))}

              <button type="button" style={s.btnAddFoto} onClick={() => cameraInputRef.current?.click()}>
                <span style={{ fontSize: 24, lineHeight: 1 }}>📷</span>
                <span style={{ fontSize: 11 }}>Kamera</span>
              </button>
              <button type="button" style={s.btnAddFoto} onClick={() => fileInputRef.current?.click()}>
                <span style={{ fontSize: 24, lineHeight: 1 }}>🖼</span>
                <span style={{ fontSize: 11 }}>Mediathek</span>
              </button>
            </div>

            <input ref={cameraInputRef} type="file" accept="image/*,.heic,.heif" capture="environment"
              onChange={handleNewFiles} style={{ display: 'none' }} />
            <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif" multiple onChange={handleNewFiles}
              style={{ display: 'none' }} />
          </div>

          {newFiles.length > 0 && (
            <div style={s.hint2}>
              {newFiles.length} Foto{newFiles.length !== 1 ? 's' : ''} wird beim Speichern hochgeladen.
            </div>
          )}

          {hasKiSuggestion && (
            <div style={s.hint2} role="status">
              KI-Vorschlag oben am Foto – „Übernehmen“ überträgt Kategorie und Stichworte.
            </div>
          )}

          <div className="box-form-actions" style={s.btnRow}>
            <button type="button" onClick={onCancel} style={s.btnCancel}>Schließen</button>
            <button type="submit" disabled={saving} style={s.btnSave}>
              {saving ? 'Speichert...' : isEdit ? 'Speichern' : 'Anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(23,32,51,0.62)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto', zIndex: 1000 },
  modal: { background: '#fff', border: '1px solid rgba(221,226,235,0.9)', borderRadius: 20, padding: 26, width: '100%', maxWidth: 620, marginTop: 16, marginBottom: 32, boxShadow: '0 24px 70px rgba(25,35,62,0.22)' },
  modalHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 22 },
  kicker: { color: '#0B6766', fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4 },
  title: { fontSize: 25, lineHeight: 1.1, letterSpacing: '-0.035em', fontWeight: 800, color: '#213A44' },
  closeButton: { width: 40, height: 40, border: 'none', borderRadius: 12, background: '#f0f2f6', color: '#657083', fontSize: 25, lineHeight: 1 },
  form: { display: 'flex', flexDirection: 'column', gap: 17 },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 700, color: '#465165' },
  input: { padding: '11px 13px', borderRadius: 11, border: '1px solid #d9dee8', fontSize: 15, width: '100%', boxSizing: 'border-box', background: '#fff', color: '#213A44' },
  hint: { fontSize: 12, lineHeight: 1.45, color: '#768094', fontWeight: 400 },
  hint2: { fontSize: 13, color: '#667085', background: '#f4f5f8', borderRadius: 11, padding: '10px 12px' },
  reminderBox: { display: 'flex', flexDirection: 'column', gap: 11, padding: 15, border: '1px solid #fed7aa', borderRadius: 14, background: '#fffaf3' },
  reminderHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
  reminderTitle: { fontSize: 13, fontWeight: 800, color: '#9a4f14' },
  reminderIntro: { marginTop: 3, color: '#8a694e', fontSize: 12, lineHeight: 1.4 },
  reminderClear: { flexShrink: 0, border: 'none', borderRadius: 8, padding: '7px 9px', background: '#ffedd5', color: '#9a3412', fontSize: 12, fontWeight: 750 },
  reminderGrid: { display: 'grid', gridTemplateColumns: 'minmax(150px, 0.7fr) minmax(220px, 1.3fr)', gap: 9 },
  locationBox: { display: 'flex', flexDirection: 'column', gap: 10, padding: 15, border: '1px solid #BCD7D0', borderRadius: 14, background: '#F0F5F1' },
  locationTitle: { fontSize: 13, fontWeight: 800, color: '#0B6766' },
  locationGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 },
  locationPreview: { borderRadius: 10, padding: '10px 11px', background: '#e8f6f1', color: '#137a62', fontSize: 14 },
  legacyLocation: { borderRadius: 8, padding: '8px 10px', background: '#fff7ed', color: '#9a3412', fontSize: 13, lineHeight: 1.4 },
  dropdown: { position: 'absolute', top: 'calc(100% + 5px)', left: 0, right: 0, background: '#fff', border: '1px solid #d9dee8', borderRadius: 12, boxShadow: '0 12px 30px rgba(23,32,51,0.14)', zIndex: 100, maxHeight: 200, overflowY: 'auto' },
  dropdownItem: { padding: '11px 13px', cursor: 'pointer', fontSize: 14, color: '#213A44' },
  fotoGrid: { display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 },
  fotoThumbWrap: { position: 'relative', width: 96, flexShrink: 0 },
  fotoThumb: { width: 96, height: 96, objectFit: 'cover', borderRadius: 12, display: 'block', cursor: 'pointer', border: '1px solid #dde2eb' },
  heicBadge: { position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', padding: 8, borderRadius: 12, background: '#DCEBE7', color: '#0B6766', fontSize: 11, fontWeight: 800, lineHeight: 1.3, textAlign: 'center', pointerEvents: 'none' },
  btnDeleteFoto: { position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, fontSize: 14, fontWeight: 700, lineHeight: '22px', textAlign: 'center', cursor: 'pointer', padding: 0 },
  btnKiThumb: { position: 'absolute', bottom: 4, right: 4, background: 'rgba(11,103,102,0.92)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, padding: '4px 6px', cursor: 'pointer' },
  newBadge: { position: 'absolute', top: 4, left: 4, background: '#0B6766', color: '#fff', borderRadius: 6, fontSize: 10, fontWeight: 700, padding: '2px 6px' },
  kiPopup: { position: 'absolute', left: 0, right: 0, top: 100, background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 8, padding: 8, zIndex: 10, minWidth: 200, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
  kiPopupText: { fontSize: 12, marginBottom: 6, lineHeight: 1.4, color: '#1e293b' },
  kiPopupBtns: { display: 'flex', gap: 6 },
  btnApply: { background: '#059669', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flex: 1 },
  btnIgnore: { background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6, padding: '5px 8px', fontSize: 12, cursor: 'pointer' },
  btnAddFoto: { width: 96, height: 96, border: '1.5px dashed #c8ceda', borderRadius: 12, background: '#f7f8fa', color: '#667085', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: 3, flexShrink: 0 },
  btnRow: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6, paddingTop: 16, borderTop: '1px solid #edf0f4' },
  btnCancel: { background: '#eef0f5', color: '#465165', border: 'none', borderRadius: 11, padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnSave: { background: '#0B6766', color: '#fff', border: 'none', borderRadius: 11, padding: '12px 25px', fontSize: 14, fontWeight: 800, cursor: 'pointer', boxShadow: '0 7px 18px rgba(11,103,102,0.20)' },
  error: { background: '#fff2f4', color: '#971a32', border: '1px solid #f1bcc6', borderRadius: 11, padding: '11px 13px', fontSize: 13, marginBottom: 12 },
  successMsg: { background: '#eaf7f2', color: '#137a62', border: '1px solid #cce8df', borderRadius: 11, padding: '11px 13px', fontSize: 13, fontWeight: 700, marginBottom: 12 },
  kiLoadingBar: { background: '#faf5ff', border: '1.5px solid #e9d5ff', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: '#6b21a8', display: 'flex', alignItems: 'center', gap: 8 },
  kiSpinner: { fontSize: 18 },
  lightbox: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  lightboxClose: { position: 'fixed', top: 16, right: 16, background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', borderRadius: '50%', width: 44, height: 44, fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  lightboxImg: { maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 },
};
